import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE } from "../api/base";
import { useAuth } from "../context/AuthContext";

export default function EditUser() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { id } = useParams();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("technician");
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = localStorage.getItem("is_super_admin") === "true";

  // -------- Load user (SUPER ADMIN ONLY) --------
  const loadUser = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 403) {
        alert("You are not allowed to view this user.");
        navigate("/");
        return;
      }

      if (response.status === 404) {
        alert("User not found.");
        navigate("/users");
        return;
      }

      if (!response.ok) {
        alert(data.detail || "Could not load user");
        navigate("/users");
        return;
      }

      setFullName(data.full_name || "");
      setEmail(data.email || "");
      setRole((data.role || "technician").toLowerCase());
    } catch (error) {
      console.error("Error loading user", error);
      alert("Could not load user.");
      navigate("/users");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, token]);

  // -------- Guard + initial load --------
  useEffect(() => {
    if (!token || !isSuperAdmin) {
      navigate("/");
      return;
    }
    loadUser();
  }, [token, isSuperAdmin, navigate, loadUser]);

  // -------- Submit changes --------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      full_name: fullName,
      email,
      role: role.toLowerCase(),
    };

    if (password) {
      payload.password = password; // only send if changed
    }

    try {
      const response = await fetch(`${API_BASE}/users/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 403) {
        alert("You are not allowed to update this user.");
        return;
      }

      if (response.status === 404) {
        alert("User not found.");
        navigate("/users");
        return;
      }

      if (!response.ok) {
        alert(data.detail || "Update failed");
        return;
      }

      alert("User updated!");
      navigate("/users");
    } catch (error) {
      console.error("Error updating user", error);
      alert("Could not update user.");
    }
  };

  if (loading) {
    return <p style={{ padding: "20px" }}>Loading user...</p>;
  }

  return (
    <div style={{ maxWidth: "500px", margin: "30px auto", padding: "20px" }}>
      <h1 style={{ marginBottom: "20px" }}>Edit User</h1>
      <p style={{ color: "#555", marginBottom: "14px" }}>
        Only super admins can edit users. Leave password empty to keep it the same.
      </p>

      <form onSubmit={handleSubmit}>
        <label style={labelStyle}>Full Name</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Role</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          style={inputStyle}
        >
          <option value="technician">Technician</option>
          <option value="host">Host</option>
          <option value="admin">Admin</option>
        </select>

        <label style={labelStyle}>Password (optional)</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Leave blank to keep current password"
          style={inputStyle}
        />

        <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
          <button type="submit" style={btnPrimary}>
            Save Changes
          </button>
          <button
            type="button"
            onClick={() => navigate("/users")}
            style={btnSecondary}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontWeight: "bold",
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  marginBottom: "12px",
  borderRadius: "6px",
  border: "1px solid #ccc",
};

const btnPrimary = {
  padding: "10px 16px",
  background: "#3498db",
  color: "white",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};

const btnSecondary = {
  padding: "10px 16px",
  background: "#bdc3c7",
  color: "#2c3e50",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};
